import { workDateTimeToLoggedAtIso } from "@lhd/features/TrackingTime/workLogTimezone";
import { EXTENDED_TIME_HOUR_COUNT } from "../../until";

export type ValidatableBreakTime = {
  start?: string;
  end?: string;
};

export type ValidatableWorkShift = {
  checkIn?: string;
  checkOut?: string;
  breaks?: Array<ValidatableBreakTime | undefined>;
};

type DisabledTimeProps = {
  disabledHours: () => number[];
  disabledMinutes: (hour: number) => number[];
};

const range = (length: number): number[] =>
  Array.from({ length }, (_, index) => index);

const minuteOfDay = (value: string): number => {
  const [hour, minute] = value.split(":").map(Number);
  return hour * 60 + minute;
};

export const minTime = (
  ...values: Array<string | undefined>
): string | undefined => {
  const available = values.filter((value): value is string => Boolean(value));
  return available.reduce<string | undefined>(
    (minimum, value) =>
      !minimum || minuteOfDay(value) < minuteOfDay(minimum) ? value : minimum,
    undefined,
  );
};

export const maxTime = (
  ...values: Array<string | undefined>
): string | undefined => {
  const available = values.filter((value): value is string => Boolean(value));
  return available.reduce<string | undefined>(
    (maximum, value) =>
      !maximum || minuteOfDay(value) > minuteOfDay(maximum) ? value : maximum,
    undefined,
  );
};

export const disabledTimeProps = (
  minimum?: string,
  maximum?: string,
  options?: {
    minimumExclusive?: boolean;
    maximumExclusive?: boolean;
  },
): DisabledTimeProps => {
  const minimumMinute = minimum
    ? minuteOfDay(minimum) + (options?.minimumExclusive ? 1 : 0)
    : 0;
  const maximumMinute = maximum
    ? minuteOfDay(maximum) - (options?.maximumExclusive ? 1 : 0)
    : EXTENDED_TIME_HOUR_COUNT * 60 - 1;
  const isAllowed = (hour: number, minute: number) => {
    const value = hour * 60 + minute;
    return value >= minimumMinute && value <= maximumMinute;
  };

  return {
    disabledHours: () =>
      range(EXTENDED_TIME_HOUR_COUNT).filter((hour) =>
        range(60).every((minute) => !isAllowed(hour, minute)),
      ),
    disabledMinutes: (hour) =>
      range(60).filter((minute) => !isAllowed(hour, minute)),
  };
};

const toTimestamp = (workDate: string, value?: string): number | null => {
  if (!value) return null;
  const iso = workDateTimeToLoggedAtIso(workDate, value);
  if (!iso) return null;
  const timestamp = Date.parse(iso);
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const validateWorkShifts = (
  workDate: string,
  shifts: ValidatableWorkShift[],
  nowMs: number,
): string[] => {
  const errors = new Set<string>();

  shifts.forEach((shift, shiftIndex) => {
    const shiftLabel = `勤務 ${shiftIndex + 1}`;
    const checkInMs = toTimestamp(workDate, shift.checkIn);
    const checkOutMs = toTimestamp(workDate, shift.checkOut);
    const isLastShift = shiftIndex === shifts.length - 1;

    if (checkInMs != null && checkInMs > nowMs) {
      errors.add(`${shiftLabel}: 出勤時間は未来の時刻に設定できません。`);
    }
    if (checkOutMs != null && checkOutMs > nowMs) {
      errors.add(`${shiftLabel}: 退勤時間は未来の時刻に設定できません。`);
    }
    if (checkInMs != null && checkOutMs != null && checkOutMs < checkInMs) {
      errors.add(`${shiftLabel}: 退勤時間は出勤時間以降に設定してください。`);
    }
    if (!isLastShift && checkOutMs == null) {
      errors.add(`${shiftLabel}: 次の勤務を登録するには退勤時間が必要です。`);
    }

    if (shiftIndex > 0) {
      const previousCheckoutMs = toTimestamp(
        workDate,
        shifts[shiftIndex - 1]?.checkOut,
      );
      if (
        previousCheckoutMs != null &&
        checkInMs != null &&
        checkInMs <= previousCheckoutMs
      ) {
        errors.add(
          `${shiftLabel}: 出勤時間は前の勤務の退勤時間より後に設定してください。`,
        );
      }
    }

    let previousBreakEndMs: number | null = null;
    const breaks = shift.breaks || [];

    breaks.forEach((breakTime, breakIndex) => {
      if (!breakTime) return;
      const breakLabel = `${shiftLabel}・休憩 ${breakIndex + 1}`;
      const breakStartMs = toTimestamp(workDate, breakTime.start);
      const breakEndMs = toTimestamp(workDate, breakTime.end);
      const hasLaterBreak = breaks
        .slice(breakIndex + 1)
        .some((item) => item?.start || item?.end);

      if (breakStartMs == null && breakEndMs == null) return;

      if (breakStartMs == null && breakEndMs != null) {
        errors.add(`${breakLabel}: 休憩開始時間を入力してください。`);
        return;
      }

      if (breakStartMs != null && breakStartMs > nowMs) {
        errors.add(`${breakLabel}: 休憩開始時間は未来の時刻に設定できません。`);
      }
      if (breakEndMs != null && breakEndMs > nowMs) {
        errors.add(`${breakLabel}: 休憩終了時間は未来の時刻に設定できません。`);
      }
      if (checkInMs != null && breakStartMs! < checkInMs) {
        errors.add(
          `${breakLabel}: 休憩開始時間は出勤時間以降に設定してください。`,
        );
      }
      if (checkOutMs != null && breakStartMs! > checkOutMs) {
        errors.add(
          `${breakLabel}: 休憩開始時間は退勤時間以前に設定してください。`,
        );
      }
      if (previousBreakEndMs != null && breakStartMs! < previousBreakEndMs) {
        errors.add(
          `${breakLabel}: 休憩時間を早い順に入力し、重複しないようにしてください。`,
        );
      }

      if (breakEndMs == null) {
        if (hasLaterBreak) {
          errors.add(
            `${breakLabel}: 次の休憩の前に休憩終了時間を入力してください。`,
          );
        }
        if (checkOutMs != null) {
          errors.add(
            `${breakLabel}: 退勤時間の前に休憩終了時間を入力してください。`,
          );
        }
        if (!isLastShift) {
          errors.add(
            `${breakLabel}: 次の勤務の前に休憩終了時間を入力してください。`,
          );
        }
        return;
      }

      if (breakEndMs < breakStartMs!) {
        errors.add(
          `${breakLabel}: 休憩終了時間は休憩開始時間以降に設定してください。`,
        );
      }
      if (checkOutMs != null && breakEndMs > checkOutMs) {
        errors.add(
          `${breakLabel}: 休憩終了時間は退勤時間以前に設定してください。`,
        );
      }
      previousBreakEndMs = breakEndMs;
    });
  });

  return Array.from(errors);
};
